import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import resourcesApi from '../api/resourcesApi';
import AccessState from './AccessState';
import usePageAccessRedirect from '../hooks/usePageAccessRedirect';

const safeHttpHref = (value) => {
	const raw = String(value || '').trim();
	if (!raw) return '';
	if (!/^https?:\/\//i.test(raw)) return '';
	if (/\s/.test(raw)) return '';

	try {
		if (typeof URL === 'function') {
			const url = new URL(raw);
			if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
			return url.toString();
		}
	} catch (_) {
		// Fall back to raw value below.
	}

	return raw;
};

const resolveResourceHref = (resource) => {
	if (!resource) return '';
	return safeHttpHref(resource.cloudFrontUrl || resource.publicUrl || resource.url);
};

const getIcon = (resource) => {
	const iconType = String((resource && (resource.iconType || resource.resourceType)) || '').toLowerCase();
	if (iconType === 'pdf') return { className: 'pi pi-file-pdf', color: 'red' };
	return { className: 'pi pi-link', color: 'turquoise' };
};

const groupByCategory = (items) => {
	const map = new Map();
	(items || []).forEach((item) => {
		const category = String((item && item.category) || '').trim() || 'Uncategorised';
		if (!map.has(category)) map.set(category, []);
		map.get(category).push(item);
	});

	const toSortNumber = (value) => {
		const num = Number(value);
		return Number.isFinite(num) ? num : null;
	};

	return Array.from(map.entries())
		.map(([category, list]) => ({
			category,
			items: list.sort((a, b) => {
				const ao = toSortNumber(a && a.sortOrder);
				const bo = toSortNumber(b && b.sortOrder);

				if (ao !== null && bo !== null && ao !== bo) return ao - bo;
				if (ao !== null && bo === null) return -1;
				if (ao === null && bo !== null) return 1;

				return String((a && a.title) || '').localeCompare(String((b && b.title) || ''));
			}),
		}))
		.sort((a, b) => a.category.localeCompare(b.category));
};

const ResourceLink = ({ resource }) => {
	const href = resolveResourceHref(resource);
	const icon = getIcon(resource);
	const title = String((resource && resource.title) || '').trim() || 'Untitled resource';

	return (
		<div className="d-flex align-items-start">
			<i className={icon.className} style={{ fontSize: '1em', color: icon.color, marginTop: '0.2rem' }} />
			{href ? (
				<a className="ms-2" href={href} target="_blank" rel="noreferrer noopener">
					{title}
				</a>
			) : (
				<span className="ms-2 text-muted">{title}</span>
			)}
		</div>
	);
};

const SupportResourcesContent = ({ appid, cid, lms }) => {
	const msgs = useRef(null);
	const { accessIssue, clearAccessIssue, handleAccessError } = usePageAccessRedirect({
		deniedTitle: 'Resources Unavailable',
		deniedMessage: 'You do not have access to resources.',
		deniedRedirectTo: '/support',
		deniedActionLabel: 'Go To Support Home',
	});

	const [loading, setLoading] = useState(true);
	const [resources, setResources] = useState([]);

	const groups = useMemo(() => groupByCategory(resources), [resources]);

	const safeLms = String(lms || '').trim();
	const appProviderId = Number(appid);
	const customerId = Number(cid);

	const load = async () => {
		if (!safeLms) {
			setResources([]);
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			clearAccessIssue();
			if (msgs.current) msgs.current.clear();

			const next = await resourcesApi.previewSupportResources({
				customerId: Number.isFinite(customerId) ? customerId : null,
				appProviderId: Number.isFinite(appProviderId) ? appProviderId : null,
				lms: safeLms,
			});

			setResources(Array.isArray(next) ? next : []);
		} catch (error) {
			if (handleAccessError(error)) return;

			const detail =
				(error && error.response && error.response.data) ||
				(error && error.message) ||
				'Failed to load resources.';

			if (msgs.current) {
				msgs.current.show({ severity: 'error', summary: '', detail, sticky: true });
			}
			setResources([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
	}, [safeLms, appid, cid]);

	const progressbar = (
		<div className="card mt-3">
			<ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
		</div>
	);

	if (accessIssue) {
		return <AccessState {...accessIssue} />;
	}

	if (!safeLms) {
		return <p>Loading resources…</p>;
	}

	return (
		<div>
			<Messages ref={msgs} />
			{loading && progressbar}

			{!loading && groups.length === 0 && <p>No resources found.</p>}

			<div className="grid">
				{groups.map((group) => (
					<div key={group.category} className="col-12 md:col-6">
						<Card title={group.category} className="h-100">
							<div className="d-flex flex-column gap-2">
								{group.items.map((resource) => (
									<ResourceLink key={resource.id || resource.title} resource={resource} />
								))}
							</div>
						</Card>
					</div>
				))}
			</div>
		</div>
	);
};

export default SupportResourcesContent;
